/** @file This file contains the store for groups. */
import * as api from '@codegrade/api';
import { Maybe } from '@codegrade/maybe';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import * as utils from '@/utils';
const storeBuilder = getStoreBuilder();
/**
 * Create an empty state object for the store module.
 *
 * @returns An empty state object for the store module.
 */
function makeInitialState() {
    return {
        perUser: {},
        byGroupSetId: {},
        perUserLoaders: {},
    };
}
const moduleBuilder = storeBuilder.module('groups', makeInitialState());
/** The groups store module. */
export var GroupsStore;
(function (GroupsStore) {
    /** Mutation to reset the data of store module back to the initial state. */
    GroupsStore.commitClearAll = moduleBuilder.commit(state => Object.assign(state, makeInitialState()), 'commitClearAll');
    const commitPerUserLoader = moduleBuilder.commit((state, { groupSetId, loader, userId, }) => {
        utils.vueSetOrDelete(utils.vueSetDefault(state.perUserLoaders, groupSetId, {}), userId, loader);
    }, 'commitPerUserLoader');
    const commitPerUser = moduleBuilder.commit((state, { groupSetId, userId, group, }) => {
        utils.vueSet(utils.vueSetDefault(state.perUser, groupSetId, {}), userId, group);
    }, 'commitPerUser');
    /** Get a getter to get groups of a given group set. */
    GroupsStore.getForGroupSet = moduleBuilder.read(
    /**
     * The method that returns the getter.
     *
     * @param state The current state of the store module.
     * @returns The getter.
     */
    state => {
        /**
         * The getter to get groups of a given group set.
         *
         * @param groupSetId Id of the group set.
         * @returns Data in the store module.
         */
        function getter(groupSetId) {
            return utils.getPaginatedState(state.byGroupSetId[groupSetId]);
        }
        return getter;
    }, 'getForGroupSet');
    /** Get a getter to get a user's group. */
    GroupsStore.getForUser = moduleBuilder.read(
    /**
     * The method that returns the getter.
     *
     * @param state The current state of the store module.
     * @returns The getter.
     */
    state => {
        /**
         * The getter.
         *
         * @param params Object with Ids.
         * @returns Maybe of a Maybe of a group.
         * Nothing: The locally available data is incomplete, we do not know if the user
         *          is in a group or not.
         * Just<Nothing>: The user does not belong to any group.
         * Just<Just<Group>: The user belongs to a group wrapped in the maybe objects.
         */
        function getter({ groupSetId, userId, }) {
            const group = state.perUser[groupSetId]?.[userId];
            if (group != null) {
                return Maybe.of(group);
            }
            const groups = GroupsStore.getForGroupSet()(groupSetId);
            for (const g of groups.data) {
                for (const member of g.members) {
                    if (member.id === userId) {
                        return Maybe.of(Maybe.of(g));
                    }
                }
            }
            if (groups.state === 'loaded-all') {
                return Maybe.of(Maybe.empty());
            }
            return Maybe.empty();
        }
        return getter;
    }, 'getForUser');
    const commitGroups = moduleBuilder.commit((state, { groupSetId, data, }) => {
        utils.vueSet(state.byGroupSetId, groupSetId, data);
    }, 'commitGroups');
    const commitGroup = moduleBuilder.commit((state, group) => {
        const data = state.byGroupSetId[group.groupSetId];
        if (data == null) {
            return;
        }
        let seen = false;
        const newInnerData = data.data?.map(g => {
            if (g.id === group.id) {
                seen = true;
                return group;
            }
            return g;
        });
        if (data.pagination.tag === 'finished' && !seen) {
            newInnerData?.push(group);
        }
        utils.vueSet(state.byGroupSetId, group.groupSetId, {
            ...data,
            data: newInnerData,
        });
        const byUser = utils.vueSetDefault(state.perUser, group.groupSetId, {});
        for (const member of group.members) {
            utils.vueSet(byUser, member.id, Maybe.of(group));
        }
    }, 'insertGroup');
    /**
     * Action to update the name of a group.
     */
    GroupsStore.updateGroupTitle = moduleBuilder.dispatch(async (_, { groupId, name, }) => {
        const { cgResult: group } = await client.p.group.renameGroup({
            groupId,
            requestBody: { name },
        });
        commitGroup(group);
    }, 'updateGroupTitle');
    /**
     * Action to load data of a given group set.
     */
    GroupsStore.loadByGroupSet = moduleBuilder.dispatch(async (ctx, { groupSetId, toLoad, }) => {
        await utils.loadPaginatedWithForce({
            commitData: data => commitGroups({ groupSetId, data }),
            getData: () => ctx.state.byGroupSetId[groupSetId],
            load: async (nextToken) => await client.p.groupSet.getAllGroups({ groupSetId, nextToken }),
            toLoad,
        });
    }, 'loadByGroupSet');
    /** Action to load group of a user in a given group set. */
    GroupsStore.loadGroupOfUser = moduleBuilder.dispatch(async (ctx, { groupSetId, userId, force = false, needsExtended = false, }) => {
        await utils.storeLoadWithMaybeForce({
            addLoader: loader => commitPerUserLoader({ userId, groupSetId, loader: Maybe.of(loader) }),
            force,
            load: async () => {
                let group;
                try {
                    const response = await client.p.groupSet.getUserGroup({
                        groupSetId,
                        userId,
                    });
                    group = Maybe.of(response.body);
                }
                catch (caught) {
                    if (caught instanceof api.ApiResultError &&
                        caught.response.status === 404) {
                        group = Maybe.empty();
                    }
                    else {
                        throw caught;
                    }
                }
                commitPerUser({ userId, group, groupSetId });
            },
            loader: Maybe.fromNullable(ctx.state.perUserLoaders[groupSetId]?.[userId]),
            removeLoader: () => commitPerUserLoader({ groupSetId, userId, loader: Maybe.empty() }),
            value: GroupsStore.getForUser()({ groupSetId, userId }).filter(x => !needsExtended || x instanceof api.ExtendedGroup),
        });
    }, 'loadGroupOfUser');
    /**
     * Action to add a user to a group.
     */
    GroupsStore.addUserToGroup = moduleBuilder.dispatch(async (_, { groupId, tenantId, username, }) => {
        const resp = await client.p.group.addMember({
            groupId,
            requestBody: { tenantId, username },
        });
        commitGroup(resp.body);
        return resp;
    }, 'addUserToGroup');
    /**
     * Aciton to remove a user from a group.
     */
    GroupsStore.removeUserFromGroup = moduleBuilder.dispatch(async (_, { groupId, userId, }) => {
        const { cgResult: group } = await client.p.group.removeMember({ groupId, userId });
        commitGroup(group);
        commitPerUser({ group: Maybe.empty(), userId, groupSetId: group.groupSetId });
    }, 'removeUserFromGroup');
    /**
     * Action to create a new group.
     */
    GroupsStore.createGroup = moduleBuilder.dispatch(async (ctx, { groupSetId, memberIds, name, }) => {
        const { cgResult: group } = await client.p.groupSet.createGroup({
            groupSetId,
            requestBody: { memberIds, name },
        });
        commitGroup(group);
    }, 'createGroup');
    /**
     * Action to remove a group.
     */
    GroupsStore.commitRemoveGroup = moduleBuilder.commit((state, { groupSetId, groupId, }) => {
        const data = state.byGroupSetId[groupSetId];
        if (data != null) {
            utils.vueSet(state.byGroupSetId, groupSetId, {
                ...data,
                data: data.data?.filter(g => g.id !== groupId),
            });
        }
    }, 'removeGroup');
    /**
     * Action to remove a group.
     */
    GroupsStore.removeGroup = moduleBuilder.dispatch(async (ctx, { groupSetId, groupId, }) => {
        const resp = await client.p.group.delete({ groupId });
        GroupsStore.commitRemoveGroup({ groupSetId, groupId });
        return resp;
    }, 'removeGroup');
    utils.signal.AFTER_USER_CHANGED.register(async () => {
        GroupsStore.commitClearAll();
    });
})(GroupsStore || (GroupsStore = {}));
